import { Link } from "react-router";
import { ArrowRight, Award, Building2, Globe, Leaf, Printer, Target, Zap, Package } from "lucide-react";
import { GradientSectionHeading } from "../components/GradientSectionHeading";

/** Equipment photos live in `public/equipment/`. */
const EQUIPMENT_IMAGE_BASE = "/equipment";

const ownedEquipment: { title: string; subtitle?: string; file: string; alt: string }[] = [
  {
    title: 'Heidelberg SM-74',
    subtitle: "SpeedMaster 5-Colour",
    file: "team-1.jpg",
    alt: "Heidelberg SM-74 SpeedMaster 5-colour offset printing press — Colour Club Printers Faisalabad",
  },
  {
    title: "Heidelberg GTO",
    file: "team-2.jpg",
    alt: "Heidelberg GTO offset printing press — best printing press Faisalabad Pakistan",
  },
  {
    title: "Heidelberg GTO",
    subtitle: "2 Colour",
    file: "team-3.jpg",
    alt: "Heidelberg GTO 2-colour offset press — packaging manufacturer Faisalabad",
  },
  {
    title: 'Heidelberg 20"x29"',
    subtitle: "2 Colour",
    file: "team-4.jpg",
    alt: "Heidelberg 20x29 inch 2-colour press — custom packaging printing Pakistan",
  },
  {
    title: 'Heidelberg 20"x29"',
    subtitle: "2 Colour",
    file: "image_50442241.jpg",
    alt: "Heidelberg 20x29 2-colour offset press — Colour Club Printers Pakistan",
  },
  {
    title: "Solna 125",
    file: "image_50420481.jpg",
    alt: "Solna 125 offset printing press — packaging printer Faisalabad Pakistan",
  },
  {
    title: 'Heidelberg 25"x36"',
    file: "image_50417921.jpg",
    alt: "Heidelberg 25x36 inch large format press — premium packaging manufacturer Pakistan",
  },
  {
    title: 'Heidelberg 28"x40"',
    subtitle: "2 Colour",
    file: "image_50417665.jpg",
    alt: "Heidelberg 28x40 inch 2-colour large format press — Colour Club Printers Faisalabad",
  },
  {
    title: 'Heidelberg 28"x40"',
    subtitle: "2 Colour",
    file: "image_50389761.jpg",
    alt: "Heidelberg 28x40 2-colour offset press — best printing press Pakistan",
  },
];

export function FacilityPage() {
  return (
    <div className="min-h-screen">
      <section className="relative bg-[#F6F2EB] py-64 overflow-hidden">
        {/* Animated Grid Background */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-50">
          <svg width="100%" height="100%" viewBox="0 0 1920 720" preserveAspectRatio="xMidYMid slice" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ shapeRendering: "geometricPrecision" }}>
            <defs>
              <radialGradient id="paint0_radial" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(960 360.002) rotate(180) scale(711 897.564)">
                <stop offset="0.236637" stopColor="#F7F4ED"></stop>
                <stop offset="0.727046" stopColor="#FF8819"></stop>
                <stop offset="0.817006" stopColor="#FF339E"></stop>
                <stop offset="0.91787" stopColor="#FFA6F9"></stop>
                <stop offset="1" stopColor="#4E93FF"></stop>
              </radialGradient>
            </defs>
            <g id="vertical-lines">
              {[64, 128, 192, 256, 320, 384, 448, 512, 576, 640, 704, 768, 832, 896, 960, 1024, 1088, 1152, 1216, 1280, 1344, 1408, 1472, 1536, 1600, 1664, 1728, 1792, 1856].map((x) => {
                const delay = 0.05 * ((960 - Math.abs(x - 960)) / 64);
                return (
                  <g key={`v-${x}`}>
                    <path d={`M${x} 0V360`} stroke="url(#paint0_radial)" strokeWidth="1.8" style={{ strokeDasharray: 360, strokeDashoffset: 360, animation: `0.5s ease-out ${delay.toFixed(2)}s 1 normal forwards running drawLine` }} />
                    <path d={`M${x} 720V360`} stroke="url(#paint0_radial)" strokeWidth="1.8" style={{ strokeDasharray: 360, strokeDashoffset: 360, animation: `0.5s ease-out ${delay.toFixed(2)}s 1 normal forwards running drawLine` }} />
                  </g>
                );
              })}
            </g>
            <g id="horizontal-lines">
              {[65, 130, 195, 260, 325, 390, 455, 520, 585, 650, 715].map((y) => {
                const delay = 0.05 * ((390 - Math.abs(y - 390)) / 65);
                return (
                  <g key={`h-${y}`}>
                    <path d={`M0 ${y}H960`} stroke="url(#paint0_radial)" strokeWidth="1.8" style={{ strokeDasharray: 960, strokeDashoffset: 960, animation: `0.5s ease-out ${delay.toFixed(2)}s 1 normal forwards running drawLine` }} />
                    <path d={`M1920 ${y}H960`} stroke="url(#paint0_radial)" strokeWidth="1.8" style={{ strokeDasharray: 960, strokeDashoffset: 960, animation: `0.5s ease-out ${delay.toFixed(2)}s 1 normal forwards running drawLine` }} />
                  </g>
                );
              })}
            </g>
          </svg>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 flex items-center justify-center">
          <div className="flex flex-col items-center text-center">
            <div className="relative max-w-4xl">
              <GradientSectionHeading
                as="h1"
                className="mb-6 text-[48px] sm:text-[60px] md:text-[70px] lg:text-[78.1px] font-bold leading-[1.1] [text-wrap:wrap]"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Our Facility
              </GradientSectionHeading>
              <p className="mx-auto h-auto w-full max-w-[720px] px-[7.6px] text-center text-[18.8px] font-bold text-[#131312] transition-colors duration-300 mb-10">
                Simple, transparent, and efficient from inquiry to delivery.
                <br />
                Experience seamless packaging production.
              </p>
              <div className="flex justify-center mt-10">
                <Link
                  to="/contact"
                  className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[#131312] text-sm font-medium text-white transition-colors hover:bg-[#1c1c1b] shadow-[0_4px_6px_-4px_rgba(0,0,0,0.22),0_10px_15px_-3px_rgba(0,0,0,0.18)] sm:h-14 sm:w-auto sm:px-8 sm:text-base"
                >
                  Start Your Project
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex h-[36px] items-center justify-center rounded-full border border-[rgba(0,0,0,0.08)] bg-[#F6F2EB] px-5 text-sm font-medium text-foreground mb-6">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2">
                <g clipPath="url(#clip0_420_10265)">
                  <path d="M4 14.6666V2.66659C4 2.31296 4.14048 1.97382 4.39052 1.72378C4.64057 1.47373 4.97971 1.33325 5.33333 1.33325H10.6667C11.0203 1.33325 11.3594 1.47373 11.6095 1.72378C11.8595 1.97382 12 2.31296 12 2.66659V14.6666H4Z" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3.99967 8H2.66634C2.31272 8 1.97358 8.14048 1.72353 8.39052C1.47348 8.64057 1.33301 8.97971 1.33301 9.33333V13.3333C1.33301 13.687 1.47348 14.0261 1.72353 14.2761C1.97358 14.5262 2.31272 14.6667 2.66634 14.6667H3.99967" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 6H13.3333C13.687 6 14.0261 6.14048 14.2761 6.39052C14.5262 6.64057 14.6667 6.97971 14.6667 7.33333V13.3333C14.6667 13.687 14.5262 14.0261 14.2761 14.2761C14.0261 14.5262 13.687 14.6667 13.3333 14.6667H12" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.66699 4H9.33366" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.66699 6.66675H9.33366" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.66699 9.33325H9.33366" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.66699 12H9.33366" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <defs>
                  <clipPath id="clip0_420_10265">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              State-of-the-Art Facility
            </div>
            <h2 className="text-4xl md:text-6xl font-semibold leading-[1.1] tracking-tight mb-6">Our Manufacturing Capabilities</h2>
            <p className="text-xl text-[#131312] max-w-3xl mx-auto">
              Advanced equipment and expert teams delivering world-class packaging at scale
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
            <div className="lg:col-span-2 relative rounded-3xl overflow-hidden h-[508px] group">
              <img
                src="/facility1.png"
                alt="Colour Club Printers 22,500 sq ft manufacturing facility — custom packaging manufacturer Faisalabad Pakistan"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-8 left-8 text-white">
                <h3 className="text-2xl font-semibold mb-2 text-[#FBFAF7]">22,500 sq ft Facility</h3>
                <p className="text-white/90">Purpose-built for precision manufacturing</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="relative rounded-3xl overflow-hidden h-[242px] group">
                <img
                  src="/facility2.png"
                  alt="Heidelberg offset printing presses — best printing press Faisalabad Pakistan"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-6 left-6 text-white">
                  <h3 className="text-xl font-bold text-[#FBFAF7]">Printing Presses</h3>
                </div>
              </div>
              <div className="relative rounded-3xl overflow-hidden h-[242px] group">
                <img
                  src="/facility3.png"
                  alt="Custom packaging production floor — printing press manufacturer Faisalabad Pakistan"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-6 left-6 text-white">
                  <h3 className="text-xl font-bold text-[#FBFAF7]">Production Floor</h3>
                  <p className="text-white/90">Mon-Sat, 9 AM - 6 PM</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="flex flex-col items-start p-12 gap-8 border border-[rgba(0,0,0,0.08)] bg-[#F6F2EB] rounded-[24px]">
              <h3 className="text-2xl font-semibold text-[#131312]">Production Facility</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-none flex items-center justify-center size-12 border border-[rgba(0,0,0,0.08)] bg-[#FBFAF7] rounded-xl text-[#131312]">
                    <Building2 className="size-6" />
                  </div>
                  <div>
                    <div className="font-bold text-lg mb-1 text-[#131312]">22,500 sq ft Facility</div>
                    <div className="text-[#131312]/80">Purpose-built manufacturing and warehouse space</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-none flex items-center justify-center size-12 border border-[rgba(0,0,0,0.08)] bg-[#FBFAF7] rounded-xl text-[#131312]">
                    <Zap className="size-6" />
                  </div>
                  <div>
                    <div className="font-bold text-lg mb-1 text-[#131312]">Dedicated Operations</div>
                    <div className="text-[#131312]/80">Production running Mon-Sat, 9 AM to 6 PM</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-none flex items-center justify-center size-12 border border-[rgba(0,0,0,0.08)] bg-[#FBFAF7] rounded-xl text-[#131312]">
                    <Globe className="size-6" />
                  </div>
                  <div>
                    <div className="font-bold text-lg mb-1 text-[#131312]">Global Distribution</div>
                    <div className="text-[#131312]/80">Strategically located for UK, EU, and USA markets</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-none flex items-center justify-center size-12 border border-[rgba(0,0,0,0.08)] bg-[#FBFAF7] rounded-xl text-[#131312]">
                    <Leaf className="size-6" />
                  </div>
                  <div>
                    <div className="font-bold text-lg mb-1 text-[#131312]">Eco-Friendly Systems</div>
                    <div className="text-[#131312]/80">Energy-efficient operations with waste recycling programs</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start p-12 gap-8 border border-[rgba(0,0,0,0.08)] bg-[#F6F2EB] rounded-[24px]">
              <h3 className="text-2xl font-semibold text-[#131312]">Advanced Equipment</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-none flex items-center justify-center size-12 border border-[rgba(0,0,0,0.08)] bg-[#FBFAF7] rounded-xl text-[#131312]">
                    <Zap className="size-6" />
                  </div>
                  <div>
                    <div className="font-bold text-lg mb-1 text-[#131312]">8-Color Offset Presses</div>
                    <div className="text-[#131312]/80">Heidelberg and KBA high-speed printing equipment</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-none flex items-center justify-center size-12 border border-[rgba(0,0,0,0.08)] bg-[#FBFAF7] rounded-xl text-[#131312]">
                    <Package className="size-6" />
                  </div>
                  <div>
                    <div className="font-bold text-lg mb-1 text-[#131312]">Digital Print Stations</div>
                    <div className="text-[#131312]/80">HP Indigo for short runs and variable data</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-none flex items-center justify-center size-12 border border-[rgba(0,0,0,0.08)] bg-[#FBFAF7] rounded-xl text-[#131312]">
                    <Target className="size-6" />
                  </div>
                  <div>
                    <div className="font-bold text-lg mb-1 text-[#131312]">Finishing Lines</div>
                    <div className="text-[#131312]/80">Foil stamping, embossing, die-cutting, and lamination</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-none flex items-center justify-center size-12 border border-[rgba(0,0,0,0.08)] bg-[#FBFAF7] rounded-xl text-[#131312]">
                    <Award className="size-6" />
                  </div>
                  <div>
                    <div className="font-bold text-lg mb-1 text-[#131312]">Quality Control Systems</div>
                    <div className="text-[#131312]/80">Automated inspection and color management technology</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
            <div className="flex flex-col items-center text-center p-8 gap-2 bg-[#F6F2EB] rounded-2xl border border-[rgba(0,0,0,0.08)]">
              <div className="text-4xl font-bold text-[#131312]">15+</div>
              <div className="text-sm font-medium text-[#131312]">Printing Presses</div>
            </div>
            <div className="flex flex-col items-center text-center p-8 gap-2 bg-[#F6F2EB] rounded-2xl border border-[rgba(0,0,0,0.08)]">
              <div className="text-4xl font-bold text-[#131312]">12</div>
              <div className="text-sm font-medium text-[#131312]">Finishing Machines</div>
            </div>
            <div className="flex flex-col items-center text-center p-8 gap-2 bg-[#F6F2EB] rounded-2xl border border-[rgba(0,0,0,0.08)]">
              <div className="text-4xl font-bold text-[#131312]">6</div>
              <div className="text-sm font-medium text-[#131312]">Die-Cutting Lines</div>
            </div>
            <div className="flex flex-col items-center text-center p-8 gap-2 bg-[#F6F2EB] rounded-2xl border border-[rgba(0,0,0,0.08)]">
              <div className="text-4xl font-bold text-[#131312]">200+</div>
              <div className="text-sm font-medium text-[#131312]">Expert Staff</div>
            </div>
          </div>

          <div className="bg-[#F6F2EB] border border-[rgba(0,0,0,0.08)] rounded-3xl p-12">
            <div className="text-center mb-12">
              <h3 className="text-2xl font-semibold mb-4">Inside Our Facility</h3>
              <p className="text-lg text-[#131312] max-w-3xl mx-auto">
                A closer look at our state-of-the-art equipment and production environment
              </p>

            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] group">
                <img
                  src="/inside1.png"
                  alt="Advanced printing equipment room — offset press packaging manufacturer Colour Club Printers"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="font-bold">Equipment Room</div>
                  <div className="text-sm text-white/80">Advanced machinery</div>
                </div>
              </div>

              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] group">
                <img
                  src="/inside2.png"
                  alt="Heidelberg offset printing press machine — premium packaging printer Pakistan"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="font-bold">Printing Press</div>
                  <div className="text-sm text-white/80">High-precision equipment</div>
                </div>
              </div>

              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] group">
                <img
                  src="/inside3.png"
                  alt="Custom packaging manufacturing line — Colour Club Printers Faisalabad Pakistan"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="font-bold">Manufacturing Line</div>
                  <div className="text-sm text-white/80">Production in action</div>
                </div>
              </div>

              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] group">
                <img
                  src="/inside4.png"
                  alt="ISO 9001 quality control inspection station — certified packaging manufacturer Pakistan"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="font-bold">Quality Control</div>
                  <div className="text-sm text-white/80">Inspection station</div>
                </div>
              </div>

              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] group">
                <img
                  src="/inside5.png"
                  alt="Expert packaging production team — Colour Club Printers Faisalabad"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="font-bold">Expert Team</div>
                  <div className="text-sm text-white/80">Skilled professionals</div>
                </div>
              </div>

              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] group">
                <img
                  src="/inside6.png"
                  alt="Mon-Sat, 9 AM-6 PM custom packaging production floor — best printing press Faisalabad Pakistan"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="font-bold">Production Floor</div>
                  <div className="text-sm text-white/80">Mon-Sat, 9 AM-6 PM</div>
                </div>
              </div>
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#131312] text-white transition-colors hover:bg-[#131312]/90 rounded-xl font-medium shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)]"
              >
                Schedule a Facility Tour
                <ArrowRight className="ml-2 size-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
